'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import PlaaMenu, { PlaaActiveItem } from './plaa-menu';
import PlaaBackButton from './plaa-back-btn';
import { useAlignmentAssetsAnalytics } from '@/analytics/alignment-assets.analytics';
import styles from '@/app/alignment-asset/plaa.module.css';

interface PlaaLayoutWrapperProps {
  readonly children: React.ReactNode;
  readonly isLoggedIn?: boolean;
}

// Map pathname to menu active item, page title, and optional viewing round
const getPageInfo = (
  pathname: string,
): { activeItem: PlaaActiveItem | undefined; title: string; viewingRound?: number } => {
  const segments = pathname.split('/').filter(Boolean);
  const pathSegment = segments[segments.length - 1] || 'overview';

  // Check if viewing a specific round (e.g., /alignment-asset/rounds/2)
  if (segments.includes('rounds') && segments.length > 2) {
    const roundNumber = parseInt(pathSegment, 10);
    if (!isNaN(roundNumber)) {
      return { activeItem: undefined, title: `Round ${roundNumber}`, viewingRound: roundNumber };
    }
  }

  if (pathSegment === 'alignment-asset' || pathSegment === 'rounds') {
    return { activeItem: 'home', title: 'Home', viewingRound: undefined };
  }

  const pageMap: Record<string, { activeItem: PlaaActiveItem; title: string }> = {
    overview: { activeItem: 'overview', title: 'Overview' },
    activities: { activeItem: 'activities', title: 'Activities' },
    leaderboard: { activeItem: 'leaderboard', title: 'Leaderboard' },
    profile: { activeItem: 'profile', title: 'Profile' },
    kudos: { activeItem: 'kudos', title: 'Kudos' },
    'terms-of-use': { activeItem: 'terms-of-use', title: 'Terms of Use' },
    'privacy-policy': { activeItem: 'privacy-policy', title: 'Privacy Policy' },
    'product-versions': { activeItem: 'product-versions', title: 'Product Versions' },
    'portfolio-holdings': { activeItem: 'trust-holdings', title: 'Portfolio & Holdings' },
    faqs: { activeItem: 'faqs', title: 'FAQ' },
    disclosure: { activeItem: 'disclosure', title: 'Disclosure' },
  };

  return pageMap[pathSegment] || pageMap['overview'];
};

export default function PlaaLayoutWrapper({ children, isLoggedIn }: PlaaLayoutWrapperProps) {
  const pathname = usePathname();
  const { activeItem, title } = getPageInfo(pathname ?? '');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { onMobileNavMenuClicked, onMobileNavMenuClosed } = useAlignmentAssetsAnalytics();

  // Scroll to top when pathname changes (tab switch)
  useEffect(() => {
    if (window.location.hash) {
      return;
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  // Close mobile menu on pathname change (navigation)
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const handleOpenMenu = () => {
    setIsMobileMenuOpen(true);
    onMobileNavMenuClicked();
  };

  const handleCloseMenu = () => {
    setIsMobileMenuOpen(false);
    onMobileNavMenuClosed();
  };

  return (
    <>
      <div className={styles.plaa}>
        {/* Mobile Menu Button */}
        <button
          className={styles.plaa__menuBtn}
          onClick={handleOpenMenu}
          aria-label="Open menu"
          aria-expanded={isMobileMenuOpen}
        >
          <img src="/icons/menu-icon.svg" alt="Menu" width={16} height={16} />
        </button>

        {/* Main Layout */}
        <div className={styles.plaa__main}>
          {/* Fixed Sidebar */}
          <aside className={styles.plaa__sidebar}>
            <PlaaMenu activeItem={activeItem} isLoggedIn={isLoggedIn} />
          </aside>

          <div className={`${styles.plaa__content} ${activeItem === 'home' ? styles['plaa__content--fluid'] : ''}`}>
            <div className={styles['plaa__content-inner']}>{children}</div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className={styles.plaa__menuBackdrop} onClick={handleCloseMenu} role="dialog" aria-modal="true">
            <div className={styles.plaa__menuOverlay} onClick={(e) => e.stopPropagation()} aria-label="Navigation menu">
              <div className={styles.plaa__menuHeader}>
                <h2 className={styles.plaa__menuTitle}>Menu</h2>
                <button className={styles.plaa__menuClose} onClick={handleCloseMenu} aria-label="Close menu">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <PlaaMenu activeItem={activeItem} onMenuItemClick={handleCloseMenu} isLoggedIn={isLoggedIn} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
